import { Typography } from "@mui/material";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, FlexColBox } from "../../../common/components";
import config from "../../../config";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { ComboItems, ProductLineType } from "../../../types";
import { useCurrency } from "../../../common/hooks/useCurrency";
import CartItemPreviewPricingInfo from "../CartPreview/components/CartItemPreviewPricingInfo";
import { FormContext } from "../../../context/Form";
import { useSelector } from "../../../common/hooks/useSelector";
import CartItemPreviewCustomLabel from "../CartPreview/components/CartItemPreviewCustomLabel";

const styles = createStyles({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    gap: "12px",
  },
  description: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: { xs: spacing.xsMobile, lg: spacing.xsDesktop },
  },
  imgContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minWidth: spacing.xlMobile,
    maxWidth: spacing.xlMobile,
    py: "3px",
    "&>img": {
      maxHeight: "58px",
    },
  },
  bundleImgContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minWidth: spacing.largeDesktop,
    maxWidth: spacing.largeDesktop,
    py: "3px",
    "&>img": {
      maxHeight: "36px",
    },
  },
  bundleContainer: {
    display: "flex",
    flexDirection: "column",
    pl: "36px",
    gap: "8px",
  },
  bundle: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
});

interface Props {
  comboItems: ComboItems;
}

const CartComboItemsPreview = ({ comboItems }: Props) => {
  const { t, i18n } = useTranslation();
  const { items, aboveMinimumQuantity, belowMaximumQuantity, familyName } =
    comboItems;
  const isBundle = items.length > 1;
  const bundleName = isBundle
    ? t("specs.bundleName", {
        familyName: t(`specs.bundleFamily.${familyName}`)[
          i18n.language === "fr" ? "toLowerCase" : "toString"
        ](),
      })
    : "";
  const { NarrowCurrencyFormatter, getCurrency } = useCurrency();
  const { formik } = useContext(FormContext);
  const shippingAddress = formik?.values?.shippingAddress;
  const { showDistributorPrices } = useSelector((state) => state.cart);

  const deprecatedDesign = useMemo(() => {
    return (
      (items.some((item) => item.deprecatedDesign) &&
        config.blockPaymentWhenDesignIsDeprecated) ||
      false
    );
  }, [items]);

  const hasExtraSizes = useMemo(
    () => items.some((item) => item.numberOfExtraSize > 0),
    [items]
  );

  const notAvailableInUS =
    items.some((item) =>
      item.productName.toUpperCase().includes(ProductLineType.CLASSIC)
    ) &&
    (getCurrency() === "usd" || shippingAddress.country === "US");

  const hasDistributorPrices = useMemo(
    () => items.every((item) => !!item.distributor),
    [items]
  );

  const { numberOfExtraSize, totalPricePerUnit, extraSizeTotal } = {
    numberOfExtraSize: items.reduce(
      (acc, item) =>
        acc +
        (showDistributorPrices && hasDistributorPrices
          ? (item.distributor?.numberOfExtraSize ?? 0)
          : item.numberOfExtraSize),
      0
    ),
    totalPricePerUnit: items.reduce(
      (acc, item) =>
        acc +
        (showDistributorPrices && hasDistributorPrices
          ? item.quantity * (item.distributor?.pricePerUnit ?? 0)
          : item.quantity * item.pricePerUnit),
      0
    ),
    extraSizeTotal: items.reduce(
      (acc, item) =>
        acc +
        (showDistributorPrices && hasDistributorPrices
          ? (item.distributor?.extraSizeTotal ?? 0)
          : item.extraSizeTotal),
      0
    ),
  };

  const hasCustomLabel = useMemo(() => {
    return items.some((item) =>
      item.creditDetails?.some((x) =>
        x.displayLabels.includes("customLabelRI2025")
      )
    );
  }, [items]);

  return (
    <FlexColBox gap={"8px"}>
      <Box sx={styles.container}>
        <Box sx={styles.imgContainer}>
          <img alt="Design" src={items[0].productUrls[0]} />
        </Box>
        <Box
          display={"flex"}
          width={"100%"}
          maxWidth={`calc(100% - 12px - ${spacing.xlMobile})`}
          alignItems={hasExtraSizes ? "flex-end" : "center"}
        >
          <Box sx={styles.description}>
            <Typography
              variant="textRegularBold"
              textOverflow={"ellipsis"}
              overflow={"hidden"}
              whiteSpace={"nowrap"}
              maxWidth={215}
            >
              {isBundle
                ? bundleName
                : items[0].designName || t("design.untitled_design")}
            </Typography>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              fullWidth
            >
              <CartItemPreviewPricingInfo
                deprecatedDesign={deprecatedDesign}
                item={items[0]}
                comboItems={comboItems}
              />
              {!deprecatedDesign &&
                aboveMinimumQuantity &&
                belowMaximumQuantity &&
                !notAvailableInUS && (
                  <Typography variant="textRegular">
                    {NarrowCurrencyFormatter?.format(totalPricePerUnit)}
                  </Typography>
                )}
              {!deprecatedDesign &&
                !belowMaximumQuantity &&
                !notAvailableInUS && (
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    minWidth="fit-content"
                  >
                    <Typography variant="textRegular">
                      {t("checkout.goingbig")}
                    </Typography>
                  </Box>
                )}
            </Box>
            {hasExtraSizes && !notAvailableInUS && (
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                fullWidth
              >
                <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                  <Typography variant="textSm" color={palette.grey.primary}>
                    {`${numberOfExtraSize} x `}
                    {t("checkout.extra_sizes")}
                  </Typography>
                </Box>
                {hasExtraSizes && (
                  <Typography variant="textRegular">
                    {NarrowCurrencyFormatter?.format(extraSizeTotal)}
                  </Typography>
                )}
              </Box>
            )}
            {hasCustomLabel && <CartItemPreviewCustomLabel />}
          </Box>
        </Box>
      </Box>
      {isBundle && (
        <Box sx={styles.bundleContainer}>
          {items.map((item, index) => (
            <Box sx={styles.bundle} key={index}>
              <Box sx={styles.bundleImgContainer}>
                <img alt="Design" src={item.productUrls[0]} />
              </Box>
              <Typography variant="textRegularBold">
                {item.designName || t("design.untitled_design")}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </FlexColBox>
  );
};

export default CartComboItemsPreview;
